import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "EV", "CNG+Petrol"];

export default function RefuelEditModal({ open, onClose, refuel, vehicles, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!refuel) return setForm(null);
    setForm({
      vehicle_id: refuel.vehicle_id,
      odometer: refuel.odometer ?? "",
      litres: refuel.litres ?? "",
      rate: refuel.rate ?? "",
      amount: refuel.amount ?? "",
      station: refuel.station ?? "",
      fuel_type: refuel.fuel_type ?? "",
      refuelled_at: (refuel.refuelled_at || "").slice(0, 16),
      notes: refuel.notes ?? "",
    });
  }, [refuel]);

  if (!open || !form || !refuel) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        odometer: Number(form.odometer),
        litres: Number(form.litres),
        rate: Number(form.rate),
        amount: Number(form.amount),
        station: form.station || null,
        fuel_type: form.fuel_type || null,
        refuelled_at: form.refuelled_at ? new Date(form.refuelled_at).toISOString() : null,
        notes: form.notes || null,
      };
      await api.put(`/refuels/${refuel.id}`, payload);
      toast.success("Refuel updated");
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl p-6 space-y-4"
        data-testid="refuel-edit-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="ft-overline">ADMIN · EDIT REFUEL</p>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Modify entry</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#A3A3A3] hover:text-white p-2" data-testid="refuel-edit-close"><X size={18} /></button>
        </div>

        <div>
          <label className="ft-overline block mb-2">Vehicle</label>
          <select value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid="edit-vehicle-select">
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.number} · {v.name}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <NumInput label="Odometer" value={form.odometer} onChange={(v) => setForm({ ...form, odometer: v })} testid="edit-odometer" />
          <TxtInput type="datetime-local" label="Date & Time" value={form.refuelled_at} onChange={(v) => setForm({ ...form, refuelled_at: v })} testid="edit-datetime" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <NumInput label="Litres" value={form.litres} onChange={(v) => setForm({ ...form, litres: v })} testid="edit-litres" />
          <NumInput label="Rate ₹/L" value={form.rate} onChange={(v) => setForm({ ...form, rate: v })} testid="edit-rate" />
          <NumInput label="Amount ₹" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="edit-amount" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <TxtInput label="Station" value={form.station} onChange={(v) => setForm({ ...form, station: v })} testid="edit-station" />
          <div>
            <label className="ft-overline block mb-2">Fuel Type</label>
            <select value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid="edit-fueltype">
              <option value="">—</option>
              {FUEL_TYPES.map((f) => (<option key={f}>{f}</option>))}
            </select>
          </div>
        </div>
        <div>
          <label className="ft-overline block mb-2">Notes</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid="edit-notes" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-[#262626] px-6 py-3 text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A] transition-colors" data-testid="edit-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="bg-[#FF5A1F] text-black px-6 py-3 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors disabled:opacity-60" data-testid="edit-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
          </button>
        </div>
      </form>
    </div>
  );
}

function NumInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="ft-overline block mb-2">{label}</label>
      <input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid={testid} />
    </div>
  );
}
function TxtInput({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="ft-overline block mb-2">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid={testid} />
    </div>
  );
}
